import { API_BASE_URL } from '../config';
import api from './axiosInstance';

/**
 * Obtiene tareas paginadas del backend
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {string} search - Término de búsqueda (opcional)
 * @param {boolean} ascending - Ordenar ascendente (true) o descendente (false)
 * @param {boolean} includeServices - Incluir información de servicios en la respuesta
 * @returns {Promise} - { total, tasks, services, documents, taskTypes }
 */
export async function getTasksPaged(
  page = 1,
  pageSize = 10,
  search = null,
  status = 'Activos',
  ascending = true,
  includeServices = false,
  sortBy = null,
  desc = null
) {
  try {
    const params = {
      page,
      pageSize,
      includeServices,
    };

    // status: 'Todos' | 'Activos' | 'Inactivos' or boolean for isEnabled
    if (typeof status === 'boolean') {
      params.isEnabled = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    if (desc !== null && desc !== undefined) {
      params.desc = desc;
    } else {
      params.ascending = ascending;
    }

    const response = await api.get(`${API_BASE_URL}/TaskSaas`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      total: response.data.total || 0,
      tasks: response.data.data || [],
      services: response.data.services || [],
      documents: response.data.documents || [],
      taskTypes: response.data.taskTypes || [],
    };
  } catch (error) {
    console.error('getTasksPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de tareas.';
    throw error;
  }
}

/**
 * Obtiene los datos necesarios para el formulario (servicios, documentos, tipos de tarea)
 * @returns {Promise} - { services, documents, taskTypes }
 */
export async function getTaskFormData() {
  try {
    const response = await api.get(`${API_BASE_URL}/TaskSaas/form-data`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return {
      services: response.data.services || [],
      documents: response.data.documents || [],
      taskTypes: response.data.taskTypes || [],
    };
  } catch (error) {
    console.error('getTaskFormData failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage =
      'No se pudo obtener los datos del formulario de tareas.';
    throw error;
  }
}

/**
 * Crea una nueva tarea
 * @param {object} taskData - Datos de la tarea
 * @returns {Promise}
 */
export async function createTask(taskData) {
  try {
    const payload = {
      name: taskData.code || '',
      order: taskData.order || 0,
      code: taskData.code || '',
      description: taskData.description || '',
      serviceId: taskData.serviceId,
      taskTypeId: taskData.taskTypeId || 0,
      documentSendId: taskData.documentSendId || 0,
      startMessage: taskData.startMessage || '',
      laborCost: taskData.laborCost || 0,
      endMessage: taskData.endMessage || '',
      businessAreaId: taskData.businessAreaId || 0,
    };

    const response = await api.post(`${API_BASE_URL}/TaskSaas`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createTask failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo crear la tarea.';
    throw error;
  }
}

/**
 * Actualiza una tarea existente
 * @param {number} taskId - ID de la tarea
 * @param {object} taskData - Datos actualizados
 * @returns {Promise}
 */
export async function updateTask(taskId, taskData) {
  try {
    const payload = {
      name: taskData.name || '',
      order: taskData.order || 0,
      code: taskData.code || '',
      description: taskData.description || '',
      serviceId: taskData.serviceId,
      taskTypeId: taskData.taskTypeId || 0,
      documentSendId: taskData.documentSendId || 0,
      startMessage: taskData.startMessage || '',
      laborCost: taskData.laborCost || 0,
      endMessage: taskData.endMessage || '',
      businessAreaId: taskData.businessAreaId || 0,
    };

    const response = await api.put(
      `${API_BASE_URL}/TaskSaas/${taskId}`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('updateTask failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo actualizar la tarea.';
    throw error;
  }
}

/**
 * Obtiene una tarea específica por ID con servicios disponibles
 * @param {number} taskId - ID de la tarea
 * @returns {Promise} - { task, services, documents, taskTypes }
 */
export async function getTaskById(taskId) {
  try {
    const response = await api.get(`${API_BASE_URL}/TaskSaas/${taskId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('getTaskById failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener la tarea.';
    throw error;
  }
}

/**
 * Elimina una tarea (borrado lógico)
 * @param {number} taskId - ID de la tarea a eliminar
 * @returns {Promise}
 */
export async function deleteTask(taskId) {
  try {
    const response = await api.delete(`${API_BASE_URL}/TaskSaas/${taskId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('deleteTask failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo eliminar la tarea.';
    throw error;
  }
}
